import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDate, DURUM_COLORS } from "@/lib/format";
import DatePicker from "@/components/DatePicker";
import { X, Plus, Trash2 } from "lucide-react";

export default function PaymentDialog({ record, onClose, onChanged }) {
  const [rec, setRec] = useState(record);
  const [tarih, setTarih] = useState(new Date().toISOString().slice(0, 10));
  const [tutar, setTutar] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!tutar || Number(tutar) <= 0) {
      toast.error("Geçerli bir tutar girin");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/cari/${rec.id}/payments`, { tarih, tutar: Number(tutar) });
      setRec(data);
      setTutar("");
      onChanged(data);
      toast.success("Ödeme eklendi");
    } catch (e) {
      toast.error("Ödeme eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  const del = async (i) => {
    try {
      const { data } = await api.delete(`/cari/${rec.id}/payments/${i}`);
      setRec(data);
      onChanged(data);
      toast.success("Ödeme silindi");
    } catch (e) {
      toast.error("Ödeme silinemedi");
    }
  };

  const durumColor = DURUM_COLORS[rec.durum] || "#8C8377";

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white border border-[#E7DECC] rounded-sm w-full max-w-lg my-16" data-testid="payment-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7DECC]">
          <div>
            <div className="kicker mb-0.5">ÖDEMELER</div>
            <h3 className="font-heading text-xl font-bold tracking-tight">{rec.musteri}</h3>
          </div>
          <button onClick={onClose} aria-label="Kapat" data-testid="payment-close" className="text-[#8C8377] hover:text-[#26231E]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-4 border border-[#E7DECC] rounded-sm divide-x divide-[#E7DECC] mb-5">
            <Stat label="Tutar" value={fmt(rec.tutar)} />
            <Stat label="Ödenen" value={fmt(rec.odenen)} color="#2F6E4E" />
            <Stat label="Kalan" value={fmt(rec.kalan)} color={rec.kalan > 0 ? "#A63A3A" : "#2F6E4E"} />
            <div className="px-3 py-3">
              <div className="kicker mb-1">Durum</div>
              <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-semibold" style={{ backgroundColor: `${durumColor}1a`, color: durumColor }} data-testid="payment-durum">
                {rec.durum}
              </span>
            </div>
          </div>

          <div className="kicker mb-2">ÖDEME EKLE</div>
          <div className="flex items-end gap-2 mb-5">
            <div className="flex-1">
              <DatePicker value={tarih} onChange={setTarih} testId="payment-date-input" />
            </div>
            <input
              type="number"
              step="0.01"
              value={tutar}
              onChange={(e) => setTutar(e.target.value)}
              data-testid="payment-amount-input"
              className="w-36 px-3 py-2 border border-[#E7DECC] rounded-sm tabular-nums focus:outline-none focus:border-[#B8933F]"
              placeholder="Tutar"
            />
            <button onClick={add} disabled={saving} data-testid="payment-add-button" className="flex items-center gap-1 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60">
              <Plus className="w-4 h-4" /> Ekle
            </button>
          </div>

          <div className="kicker mb-2">ÖDEME GEÇMİŞİ</div>
          <div className="border border-[#E7DECC] rounded-sm divide-y divide-[#E7DECC] max-h-56 overflow-auto" data-testid="payment-list">
            {rec.odemeler.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-[#8C8377]">Henüz ödeme yok.</div>
            ) : (
              rec.odemeler.map((o, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2" data-testid={`payment-item-${i}`}>
                  <span className="text-sm tabular-nums text-[#8C8377]">{fmtDate(o.tarih)}</span>
                  <span className="text-sm font-medium tabular-nums text-[#2F6E4E]">{fmt(o.tutar)}</span>
                  <button onClick={() => del(i)} aria-label="Ödemeyi sil" data-testid={`payment-delete-${i}`} className="text-[#8C8377] hover:text-[#A63A3A] transition-colors duration-150">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button onClick={onClose} data-testid="payment-done-button" className="px-6 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="px-3 py-3">
      <div className="kicker mb-1">{label}</div>
      <div className="font-heading font-bold text-sm tabular-nums" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}
